import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Gift, PartyPopper, Heart, Users, Sparkles, Calendar, CheckCircle } from 'lucide-react';
import customOrderData from '../content/customOrder.json';

const iconMap = {
  Gift: Gift,
  PartyPopper: PartyPopper,
  Heart: Heart,
  Users: Users,
  Sparkles: Sparkles,
  Calendar: Calendar,
  CheckCircle: CheckCircle,
};

const CustomOrderSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { sectionTitle, description, occasions: occasionsData, features: featuresData, processSteps: processStepsData, flexibilityTitle, flexibilityText } = customOrderData;

  const occasions = occasionsData.map(occasion => ({
    ...occasion,
    icon: React.createElement(iconMap[occasion.icon] || Gift, { size: 36 })
  }));

  const features = featuresData.map(feature => ({
    ...feature,
    icon: React.createElement(iconMap[feature.icon] || Sparkles, { size: 24 })
  }));

  return (
    <section id="custom-order" className="section-padding bg-gradient-to-b from-nitzan-light-bg to-white">
      <div className="container-width">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Gift className="text-nitzan-teal" size={20} />
            <span className="text-nitzan-teal font-medium">בהזמנה אישית</span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">{sectionTitle}</span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        {/* Occasions Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {occasions.map((occasion, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden border border-nitzan-sage/20 hover:shadow-2xl transition-all duration-300"
            >
              <div className={`bg-gradient-to-r ${occasion.gradient} p-8 text-white`}>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
                  {occasion.icon}
                </div>
                <h3 className="text-3xl font-bold mb-3">{occasion.title}</h3>
                <p className="text-white/90 text-lg">{occasion.description}</p>
              </div>

              <div className="p-8">
                <h4 className="text-lg font-bold text-nitzan-teal mb-4">רעיונות לתוכן:</h4>
                <ul className="space-y-3">
                  {occasion.ideas.map((idea, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="text-nitzan-mint flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-700">{idea}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="bg-gradient-to-br from-nitzan-mint/10 to-nitzan-teal/10 rounded-3xl p-8 md:p-12 mb-16"
        >
          <h3 className="text-3xl font-bold text-nitzan-teal mb-8 text-center">למה להזמין סדנה אישית?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-nitzan-mint to-nitzan-teal rounded-full flex items-center justify-center text-white mx-auto mb-4">
                  {feature.icon}
                </div>
                <h4 className="text-lg font-bold text-nitzan-teal mb-2">{feature.title}</h4>
                <p className="text-gray-700 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-nitzan-teal mb-8 text-center">איך זה עובד?</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {processStepsData.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border-2 border-nitzan-sage/20 text-center hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-nitzan-mint to-nitzan-teal rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h4 className="text-xl font-bold text-nitzan-teal mb-2">{item.title}</h4>
                <p className="text-gray-700">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Flexibility Message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="bg-gradient-to-r from-nitzan-sage/20 via-nitzan-mint/20 to-nitzan-teal/20 rounded-3xl p-8 md:p-12 text-center mb-16"
        >
          <Sparkles className="text-nitzan-terracotta mx-auto mb-6" size={48} />
          <h3 className="text-3xl font-bold text-nitzan-teal mb-4">{flexibilityTitle}</h3>
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
            {flexibilityText}
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="text-center"
        >
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border-2 border-nitzan-mint">
            <Gift className="text-nitzan-mint mx-auto mb-6" size={48} />
            <h3 className="text-3xl font-bold text-nitzan-teal mb-4">מתחילים לתכנן?</h3>
            <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
              ספרי לי על האירוע שלך ונראה איך אפשר להפוך אותו למשהו מיוחד במינו
            </p>
            <Link to="/contact">
              <motion.div
                className="inline-block bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                בואי נדבר על זה
              </motion.div>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CustomOrderSection;

